/**
 * Launcher V2 U7a — the two-column review BODY `SequenceStepEditor` renders
 * in place of its normal single-column hand-edit fields when its optional
 * `review` prop is present (see that file's module doc for the full seedKey
 * discipline). This is the composition ONLY — subject/body rendering, both
 * body formats, the token-gap warning row, and the DOM-island discipline all
 * still live in (and are driven by) `SequenceStepEditor`; this file never
 * re-implements a step card of its own, per this bead's "Do NOT Create"
 * rule.
 *
 * LEFT "Current" — the step's SAVED content (the `step` prop, unaffected by
 * anything the rep does here), read-only. RIGHT "Generated" — the model's
 * output (`review.staged`, patched by `review.edited`), editable, with
 * Accept/Reject (or, once decided, Undo).
 *
 * `unchanged` steps (the model reproduced the saved copy verbatim) render
 * neither column — {@link reviewBadgeState}'s "No change" case — matching
 * `CopyDiffView.tsx`'s existing per-row treatment of the same situation
 * (`No change — the generated copy for this step matches what is saved.`).
 *
 * `rejected` steps collapse the Generated column into a closed
 * `CollapsibleCard` (uncontrolled `defaultOpen={false}`, so the rep can
 * still reopen it to see what they turned down) while the Current column
 * stays fully visible — that is, after all, the copy that is staying.
 *
 * Embedded data-URI images are never rendered as real `<img>` tags in either
 * column — `maskForDisplay`/`restoreFromDisplay` (`image-mask.ts`, U3a) swap
 * them for a `"[image n: N KB]"` marker for display, and the Generated
 * column's `onChange` restores the real tag before handing the value to
 * `onEdit` — the store's `edited.body` is ALWAYS real HTML; display markers
 * exist only inside this file's rendering.
 */

import { useEffect, useRef, useState } from 'react';

import { Warning } from '@phosphor-icons/react';
import { Button } from '@clay-run/glaze/button';
import {
  CollapsibleCard,
  CollapsibleCardBody,
  CollapsibleCardHeader,
} from '@clay-run/glaze/collapsible-card';
import { Icon } from '@clay-run/glaze/icon';
import { InputField } from '@clay-run/glaze/input-field';
import { TextareaField } from '@clay-run/glaze/textarea-field';
import { Text } from '@clay-run/glaze/text';

import { Chip } from '@clay-run/glaze/chip';

import { RichTextBodyField } from '../../_shared/components/rich-text';
import type { CampaignMember } from '../../_shared/launcher-campaign.types';
import { buildTemplateVars } from '../../_shared/launcher-copy-tokens';
import type { CopyPreviewCampaign, CopyTokenGap } from '../copy-token-flags';
import { toSenderValue } from '../copy-token-flags';
import { reviewBadgeState } from '../generate-with-ai-logic';
import { maskForDisplay, restoreFromDisplay } from '../image-mask';
import type { StepReview } from '../generated-copy-store';
import type { SequenceStep } from '../schemas';
import { REPLY_STEP_NO_SUBJECT_TEXT } from '../step-content-rules';
import { resolveTokenTarget, type TokenTargetField } from '../token-insert';
import { useCaretTracker, type PlainTextAccess } from '../use-caret-tracker';
import { useAutosizeTextarea } from '../use-autosize-textarea';
import { SanitizedHtml } from './SanitizedHtml';
import { TokenPicker } from './TokenPicker';

/**
 * D8 (Wave C UI half) — whether the model's staged body still has, byte-exact,
 * every embedded image it started with, derived PURELY from `review.staged`/
 * `review.edited` (no local-only state, no cross-component wiring): a
 * position's occurrence count of the ORIGINAL data-URI tag inside the
 * CURRENT body (`edited.body` if the rep has typed, else `staged.body`
 * unmodified) is 0 → the rep's edit dropped it; >1 → the rep's edit
 * duplicated it. Byte-for-byte tag matching sidesteps `maskForDisplay`'s own
 * per-call renumbering (masking the CURRENT body fresh would renumber
 * whichever images survived, which is NOT the same "n" the rep saw when they
 * made the edit) — recomputing against the STAGED body's own numbering is
 * what keeps "Image n" stable and lets `GenerateWithAIDrawer`'s review-stage
 * tally recompute the SAME counts from the SAME entry, with no shared state
 * beyond `generated-copy-store.ts`'s existing `StepReview`.
 */
export function stepImageWarnings(review: Pick<StepReview, 'staged' | 'edited'>): {
  dropped: number[];
  duplicated: number[];
} {
  const stagedBody = review.staged.body ?? '';
  const currentBody = review.edited?.body ?? stagedBody;
  const { map } = maskForDisplay(stagedBody);
  const dropped: number[] = [];
  const duplicated: number[] = [];
  for (const [marker, tag] of Object.entries(map)) {
    const match = /^\[image (\d+): \d+ KB\]$/.exec(marker);
    const index = match ? Number(match[1]) : null;
    if (index === null) continue;
    const occurrences = currentBody.split(tag).length - 1;
    if (occurrences === 0) dropped.push(index);
    else if (occurrences > 1) duplicated.push(index);
  }
  return { dropped, duplicated };
}

export interface StepReviewCardProps {
  /** The step's SAVED content — the read-only "Current" column. Never mutated by anything in this file. */
  step: SequenceStep;
  bodyFormat: 'plain' | 'rich';
  review: StepReview;
  /**
   * Bumped by the caller ONLY when `review.staged` is replaced (a fresh
   * generate/regenerate) — never on a keystroke. One of the Generated
   * column's `RichTextBodyField` seedKey ingredients; see
   * `SequenceStepEditor.tsx`'s module doc for why a seedKey excludes
   * anything that changes mid-typing.
   */
  stagedGeneration: number;
  /** Same DOM-island identity ingredient as the hand-edit body's seedKey — a different step/scope is a different island. */
  draftKey: string;
  resetGeneration: number;
  clearEpoch: number;
  onEdit: (patch: { subject?: string; body?: string }) => void;
  onDecide: (decision: StepReview['decision']) => void;
  /**
   * Token gaps computed by the CALLER against the generated
   * (`edited ?? staged`) copy — see `copy-token-flags.ts#stepTokenGaps`.
   * This component has no member/campaign/sender context of its own, so it
   * only renders the existing warning-row treatment
   * (`SequencesStep.tsx`'s roster-gap banner) when handed a non-empty list;
   * it never computes gaps itself. Omitted/empty renders no row.
   */
  tokenGaps?: CopyTokenGap[];
  /**
   * U9b (R10) — the token picker's live-preview context, threaded straight
   * through from `SequenceStepEditor`'s own same-named props (see that
   * file's doc for the full `campaign !== undefined` rationale). This
   * component has no member/campaign/sender context of its own — it only
   * ever forwards what its caller resolved, same stance `tokenGaps` above
   * already takes.
   */
  sampleMember?: CampaignMember | null;
  campaign?: CopyPreviewCampaign | null;
  sender?: string | null;
  /**
   * The step's ordinal is a reply step of the bound Gong flow — the SAME
   * `isReplyAtOrdinal` verdict `SequenceStepEditor` uses to hide its own
   * hand-edit Subject (see that file's `:474-489` comment for the Gong 400
   * behind it). Threaded, never derived here: this component has no flow
   * context. Both columns gate on it — a reply step has no subject to show
   * and no subject to generate, and `step.subject` is NOT the signal (it is
   * `optional`, never `null`, and an imported reply step can carry one).
   */
  isReplyStep: boolean;
}

export function StepReviewCard({
  step,
  bodyFormat,
  review,
  stagedGeneration,
  draftKey,
  resetGeneration,
  clearEpoch,
  onEdit,
  onDecide,
  tokenGaps = [],
  sampleMember = null,
  campaign,
  sender = null,
  isReplyStep,
}: StepReviewCardProps) {
  const badge = reviewBadgeState(review);
  // U9b (R10) — the Generated column's own caret tracker; a separate instance
  // from `SequenceStepEditor`'s hand-edit one (mutually exclusive branches, so
  // field-key collision is impossible either way).
  const tracker = useCaretTracker<TokenTargetField>();
  const richBodyRef = useRef<HTMLDivElement>(null);
  const sampleVars =
    sampleMember && campaign !== undefined
      ? buildTemplateVars(sampleMember, campaign, toSenderValue(sender))
      : null;
  const imageWarnings = stepImageWarnings(review);
  const generatedBodyRaw = review.edited?.body ?? review.staged.body ?? '';
  // G1 (round 4) — autosize the plain-format Generated Body to its content,
  // exactly like `SequenceStepEditor.tsx`'s hand-edit Body (`:250-257`,
  // `:373-388`). Hooks must run unconditionally (before the "No change"
  // early return below), so `generatedBodyRaw` is hoisted above that return.
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  // Codex hardening fix, same pattern as `SequenceStepEditor.tsx`'s `open`
  // remount key: when `review.decision === 'rejected'`, this field renders
  // inside a `CollapsibleCard` (below) whose panel unmounts the textarea
  // while collapsed (`keepMounted=false` default). That card is made
  // CONTROLLED here (rather than left `defaultOpen`) purely so this local
  // `rejectedOpen` boolean exists to hand to `useAutosizeTextarea` as part of
  // the remount key that changes exactly when the panel remounts — without
  // it, the effect's `[ref, value]` deps can stay unchanged across that
  // remount and the freshly-mounted node never gets its first real
  // measurement.
  const [rejectedOpen, setRejectedOpen] = useState(false);
  // Codex round-4 blocking #3 — `rejectedOpen` used to survive a full
  // Reject -> expand -> Undo -> Reject cycle: Undo doesn't unmount this
  // component (it only flips `review.decision` back to `'pending'`, which
  // renders the OTHER (non-CollapsibleCard) branch below), so the boolean
  // was still `true` from the earlier expand when the card became a
  // `CollapsibleCard` again. Reset it every time the decision is anything
  // OTHER than `'rejected'` — that covers both the StrictMode-safe "reset at
  // the top of the mount effect" case (mounting already-pending/-accepted)
  // and the real bug case (Undo, then Reject again starts closed, not
  // wherever it was left last time).
  useEffect(() => {
    if (review.decision !== 'rejected') {
      setRejectedOpen(false);
    }
  }, [review.decision]);
  // `review.decision` is included alongside `rejectedOpen` so a re-reject
  // (decision unchanged from the caller's perspective is impossible here,
  // but the SAME `rejectedOpen` value across a pending->rejected->pending
  // round trip must still count as a remount) also forces a fresh
  // measurement of the newly-mounted node.
  useAutosizeTextarea(bodyRef, generatedBodyRaw, `${rejectedOpen}:${review.decision}`);

  if (badge.label === 'No change') {
    return (
      <Text size="xs" variant="tertiary">
        No change — the generated copy for this step matches what is saved.
      </Text>
    );
  }

  const currentBody = maskForDisplay(step.body ?? '').html;
  const generatedSubject = review.edited?.subject ?? review.staged.subject ?? '';
  const { html: generatedBodyDisplay, map: imageMap } = maskForDisplay(generatedBodyRaw);
  // Same rule as the hand-edit editor — see `resolveTokenTarget`'s doc.
  const tokenTarget = resolveTokenTarget(tracker.lastField, isReplyStep);
  // Both arms read the resolved `edited ?? staged` value, never the staged head.
  const tokenAccess: Record<TokenTargetField, PlainTextAccess> = {
    subject: { getText: () => generatedSubject, setText: (subject) => onEdit({ subject }) },
    body: { getText: () => generatedBodyRaw, setText: (body) => onEdit({ body }) },
  };

  const currentColumn = (
    <div className="flex flex-1 flex-col gap-2">
      <Text size="xs" weight="medium" variant="tertiary">
        Current
      </Text>
      {!isReplyStep && (
        <Text size="sm" weight="medium">
          {step.subject ?? ''}
        </Text>
      )}
      {bodyFormat === 'rich' ? (
        <SanitizedHtml html={currentBody} className="text-sm" />
      ) : (
        <Text size="sm" className="whitespace-pre-wrap">{step.body ?? ''}</Text>
      )}
    </div>
  );

  const generatedFields = (
    <div className="flex flex-1 flex-col gap-2">
      {isReplyStep ? (
        <Text size="sm" className="text-text-secondary">
          {REPLY_STEP_NO_SUBJECT_TEXT}
        </Text>
      ) : (
        <InputField
          label="Subject"
          value={generatedSubject}
          onChange={(e) => onEdit({ subject: e.target.value })}
          {...tracker.plainProps('subject')}
        />
      )}
      {bodyFormat === 'rich' ? (
        // U9b (R10) — same wrapper-div discipline as `SequenceStepEditor`'s
        // hand-edit rich body: `richProps` listens on this wrapper (not
        // `RichHtmlField`, which has no ref of its own) for the live
        // contentEditable's caret/selection.
        <div ref={richBodyRef} {...tracker.richProps('body', richBodyRef)}>
          <RichTextBodyField
            value={generatedBodyDisplay}
            seedKey={[draftKey, 'review', stagedGeneration, resetGeneration, clearEpoch]}
            onChange={(html) => onEdit({ body: restoreFromDisplay(html, imageMap).html })}
            className="mt-1 min-h-[4.5rem] w-full rounded border border-border-secondary p-2 text-sm"
            aria-label="Generated body"
          />
        </div>
      ) : (
        <TextareaField
          ref={bodyRef}
          label="Body"
          value={generatedBodyRaw}
          onValueChange={(body) => onEdit({ body })}
          resizable={false}
          style={{ minHeight: '4.5rem' }}
          {...tracker.plainProps('body')}
        />
      )}
      {/* ONE picker for this column's two fields, same as the hand-edit editor.
          A rich-format insert lands inside the MASKED display HTML and its
          `onChange` still runs through `restoreFromDisplay` above. */}
      <TokenPicker
        target={tokenTarget}
        sampleVars={sampleVars}
        sender={sender}
        onInsert={(token) => tracker.insert(tokenTarget, token, tokenAccess[tokenTarget])}
      />
      {(imageWarnings.dropped.length > 0 || imageWarnings.duplicated.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {imageWarnings.dropped.map((n) => (
            <Chip key={`dropped-${n}`}>{`Image ${n} removed`}</Chip>
          ))}
          {imageWarnings.duplicated.map((n) => (
            <Chip key={`duplicated-${n}`}>{`Image ${n} duplicated`}</Chip>
          ))}
        </div>
      )}
      {tokenGaps.length > 0 && (
        <div className="flex items-center gap-1.5 text-content-warning">
          <Icon type={Warning} />
          <Text size="xs" className="text-content-warning">
            {tokenGaps.length} unresolved merge fields
          </Text>
        </div>
      )}
      <div className="flex justify-end gap-2">
        {review.decision === 'pending' ? (
          <>
            <Button variant="secondary" size="sm" onClick={() => onDecide('rejected')}>
              Reject
            </Button>
            <Button variant="primary" size="sm" onClick={() => onDecide('accepted')}>
              Accept
            </Button>
          </>
        ) : (
          <Button variant="inline-text" size="sm" onClick={() => onDecide('pending')}>
            Undo
          </Button>
        )}
      </div>
    </div>
  );

  const generatedColumn =
    review.decision === 'rejected' ? (
      <CollapsibleCard open={rejectedOpen} onOpenChange={setRejectedOpen} className="flex-1">
        <CollapsibleCardHeader>
          <Text size="xs" weight="medium" variant="tertiary">
            Generated
          </Text>
        </CollapsibleCardHeader>
        <CollapsibleCardBody>{generatedFields}</CollapsibleCardBody>
      </CollapsibleCard>
    ) : (
      <div className="flex flex-1 flex-col gap-2">
        <Text size="xs" weight="medium" variant="tertiary">
          Generated
        </Text>
        {generatedFields}
      </div>
    );

  return (
    <div className="flex gap-4">
      {currentColumn}
      {generatedColumn}
    </div>
  );
}
